import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import orderService from '../services/orders';
import api from '../services/api';
import toast from 'react-hot-toast';

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated } = useAuth();

  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      const pendingOrder = localStorage.getItem('pendingOrder');
      if (pendingOrder) {
        completePendingOrder();
      } else {
        navigate('/dashboard', { replace: true });
      }
    }
  }, [isAuthenticated, navigate]);

  const completePendingOrder = async () => {
    const pendingOrderStr = localStorage.getItem('pendingOrder');
    if (!pendingOrderStr) return;

    try {
      const pendingOrder = JSON.parse(pendingOrderStr);
      
      const orderPayload = {
        shippingAddress: {
          name: `${pendingOrder.formData.firstName} ${pendingOrder.formData.lastName}`.trim(), 
          email: pendingOrder.formData.email, 
          phone: pendingOrder.formData.phone,
          address: `${pendingOrder.formData.address} ${pendingOrder.formData.apartment}`.trim(), 
          city: pendingOrder.formData.cityDropdown || pendingOrder.formData.city,
          province: 'Punjab', 
          postalCode: pendingOrder.formData.postalCode || '00000'
        },
        paymentMethod: pendingOrder.formData.paymentMethod,
        ...(pendingOrder.formData.paymentMethod === 'card' && pendingOrder.cardDetails && { 
          cardDetails: {
            cardNumber: pendingOrder.cardDetails.cardNumber.replace(/\s/g, ''),
            cardHolder: pendingOrder.cardDetails.cardHolder,
            expiryMonth: pendingOrder.cardDetails.expiryMonth,
            expiryYear: pendingOrder.cardDetails.expiryYear,
            cvv: pendingOrder.cardDetails.cvv
          }
        }),
        items: pendingOrder.cartItems.map(item => ({
          product: item.productId, 
          quantity: item.quantity, 
          size: item.size || '', 
          color: item.color || '',
          price: item.price, 
          productName: item.productName, 
          productImage: item.productImage
        })),
        subtotal: pendingOrder.subtotal, 
        shippingCost: pendingOrder.shippingCost, 
        discountAmount: pendingOrder.discountAmount || 0, 
        tax: 0, 
        total: pendingOrder.totalAmount
      };

      await orderService.createOrder(orderPayload);
      
      localStorage.removeItem('cart');
      localStorage.removeItem('pendingOrder');
      
      toast.success('Order placed successfully!');
      navigate('/orders');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to place your pending order. Please try again.');
      localStorage.removeItem('pendingOrder');
      navigate('/dashboard');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(formData.email, formData.password);

    if (!result.success) {
      setError(result.error);
      setLoading(false);
      return;
    }

    setLoading(false);
  };

  return (
    <div className="min-h-[80vh] bg-white flex flex-col items-center justify-center px-4 py-12">
      <div className="w-full max-w-[480px]">
        <h1 className="text-5xl font-normal tracking-widest text-center text-gray-900 uppercase mb-12">
          Login
        </h1>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 text-sm p-3 rounded mb-6 text-center">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-8">
          <div>
            <label className="block text-sm font-medium text-gray-500 uppercase tracking-wider mb-2">
              Email
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="w-full border border-gray-300 px-4 py-3 focus:outline-none focus:border-gray-900"
              required
            />
          </div>

          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="block text-sm font-medium text-gray-500 uppercase tracking-wider">
                Password
              </label>
              <Link to="/forgot-password" className="text-sm text-[var(--primary-color)] hover:underline">
                Forgot Password?
              </Link>
            </div>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              className="w-full border border-gray-300 px-4 py-3 focus:outline-none focus:border-gray-900"
              required
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-[var(--primary-color)] hover:bg-[var(--hover-primary)] disabled:bg-gray-400 text-white font-bold uppercase tracking-widest py-3 transition-colors"
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        <div className="mt-8 text-center">
          <Link to="/signup" className="text-sm text-gray-700 hover:text-black">
            Create account
          </Link>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;